import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Assignment as TaskIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Theme-aware chart colors
  const CHART_COLORS = isDarkMode 
    ? ['#88c0d0', '#5e81ac', '#b48ead', '#a3be8c', '#ebcb8b', '#d08770']
    : ['#5e81ac', '#88c0d0', '#b48ead', '#a3be8c', '#ebcb8b', '#d08770'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, chartsRes] = await Promise.all([
        axios.get('/api/stats/dashboard'),
        axios.get('/api/stats/charts?period=30'),
      ]);
      setStats(statsRes.data);
      setChartData(chartsRes.data);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка данных...
          </Typography>
        </Box>
      </Layout>
    );
  }

  const StatCard = ({ title, value, icon, gradient, subtitle, delay = 0 }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: isDarkMode 
          ? 'rgba(22, 27, 34, 0.6)'
          : '#ffffff',
        backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        animation: `fadeInUp 0.6s ease ${delay}s both`,
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: gradient,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography 
              sx={{ 
                color: 'text.secondary', 
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                background: gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  mt: 1,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDarkMode 
                ? `0 8px 24px rgba(0, 0, 0, 0.3)`
                : `0 8px 24px rgba(0, 0, 0, 0.15)`,
            }}
          >
            {React.cloneElement(icon, { sx: { color: isDarkMode ? '#0d1117' : '#ffffff', fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const tasksByStatusData = stats?.tasksByStatus?.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  const companiesByStatusData = stats?.companiesByStatus?.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: isDarkMode ? 'rgba(22, 27, 34, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            p: 1.5,
            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const axisColor = isDarkMode ? '#6e7681' : '#9ca3af';
  const gridColor = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDarkMode ? '#8b949e' : '#6b7280';

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Добро пожаловать в Nord CRM
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Обзор основных показателей системы • {new Date().toLocaleDateString('ru-RU', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Stat Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего компаний"
            value={stats?.overview?.totalCompanies || 0}
            icon={<BusinessIcon />}
            gradient="linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)"
            delay={0}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Активные задачи"
            value={stats?.overview?.activeTasks || 0}
            icon={<TaskIcon />}
            gradient="linear-gradient(135deg, #ebcb8b 0%, #d08770 100%)"
            subtitle="В работе"
            delay={0.1}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Завершено задач"
            value={stats?.overview?.completedTasks || 0}
            icon={<CheckIcon />}
            gradient="linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)"
            subtitle={`Из ${stats?.overview?.totalTasks || 0} всего`}
            delay={0.2}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Задачи в карточках"
            value={stats?.overview?.totalCardTasks || 0}
            icon={<TaskIcon />}
            gradient="linear-gradient(135deg, #5e81ac 0%, #81a1c1 100%)"
            subtitle={`Выполнено: ${stats?.overview?.completedCardTasks || 0}`}
            delay={0.25}
          />
        </Grid>

        {isAdmin && stats?.financeStats && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Баланс"
              value={`${(stats.financeStats.balance / 1000).toFixed(0)}K ₽`}
              icon={<MoneyIcon />}
              gradient="linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)"
              subtitle={`Доход: ${(stats.financeStats.totalIncome / 1000).toFixed(0)}K ₽`}
              delay={0.3}
            />
          </Grid>
        )}

        {/* Tasks Line Chart */}
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              animation: 'fadeInUp 0.6s ease 0.4s both',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: 'text.primary',
                }}
              >
                📈 Задачи за последние 30 дней
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData?.tasksByDay || []}>
                  <defs>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#88c0d0" />
                      <stop offset="100%" stopColor="#b48ead" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis 
                    dataKey="_id" 
                    stroke={axisColor}
                    tick={{ fill: textColor, fontSize: 12 }}
                  />
                  <YAxis 
                    stroke={axisColor}
                    tick={{ fill: textColor, fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: 20 }} />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="url(#lineGradient)" 
                    strokeWidth={3}
                    dot={{ fill: '#88c0d0', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#b48ead', stroke: isDarkMode ? '#fff' : '#000', strokeWidth: 2 }}
                    name="Задачи" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Tasks by Status Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              height: '100%',
              animation: 'fadeInUp 0.6s ease 0.5s both',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: 'text.primary',
                }}
              >
                📊 Задачи по статусам
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tasksByStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {tasksByStatusData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Companies by Status Bar Chart */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              animation: 'fadeInUp 0.6s ease 0.6s both',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: 'text.primary',
                }}
              >
                🏢 Компании по статусам
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={companiesByStatusData}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#88c0d0" stopOpacity={1} />
                      <stop offset="100%" stopColor="#5e81ac" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis 
                    dataKey="name" 
                    stroke={axisColor}
                    tick={{ fill: textColor, fontSize: 12 }}
                  />
                  <YAxis 
                    stroke={axisColor}
                    tick={{ fill: textColor, fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="value" 
                    fill="url(#barGradient)" 
                    name="Количество"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Manager Stats */}
        {isAdmin && stats?.managerStats && (
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                animation: 'fadeInUp 0.6s ease 0.7s both',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 3,
                    color: 'text.primary',
                  }}
                >
                  👥 Эффективность менеджеров
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.managerStats}>
                    <defs>
                      <linearGradient id="completedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a3be8c" stopOpacity={1} />
                        <stop offset="100%" stopColor="#a3be8c" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ebcb8b" stopOpacity={1} />
                        <stop offset="100%" stopColor="#d08770" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis 
                      dataKey={(item) => `${item.firstName} ${item.lastName}`} 
                      stroke={axisColor}
                      tick={{ fill: textColor, fontSize: 12 }}
                    />
                    <YAxis 
                      stroke={axisColor}
                      tick={{ fill: textColor, fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      dataKey="completedTasks" 
                      fill="url(#completedGradient)" 
                      name="Завершено задач"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar 
                      dataKey="totalTasks" 
                      fill="url(#totalGradient)" 
                      name="Всего задач"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Layout>
  );
};

export default Dashboard;
